import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../components/ui/dialog';
import { Plus, ClipboardList, Filter, Eye, Printer, Download, Mail, MessageSquare, MoreHorizontal, FilePlus, Trash2, Upload, FolderOpen, FileOutput } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { DragDropUploader, AttachmentsList } from '../components/FileUpload';
import { SectionSelectionDialog } from '../components/SectionSelectionDialog';
import { toast } from 'sonner';

// ICRCL Details
const ICRCL_NAME = "Innovative Care and Rehab Centre Limited (ICRCL)";
const ICRCL_ADDRESS = "City Gate House, 246-250 Romford Road, London, E7 9HZ";
const ICRCL_PHONE = "+44 7380 350059";
const ICRCL_EMAIL = "info@icrcl.co.uk";

export default function AssessmentsPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [disciplineFilter, setDisciplineFilter] = useState('all');
  const [disciplines, setDisciplines] = useState([]);
  const [accessLevel, setAccessLevel] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [outputDialogOpen, setOutputDialogOpen] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    type: 'custom',
    description: '',
    sections: [{ title: 'Section 1', fields: [{ name: '', type: 'text', required: false }] }]
  });

  // Check if user is main admin (Md Ashraful Alam Sujan)
  const isMainAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk' || user?.is_main_admin;

  useEffect(() => {
    fetchAssessments();
    fetchCustomTemplates();
    fetchDisciplines();
    fetchAccessLevel();
  }, [typeFilter, disciplineFilter]);

  const fetchDisciplines = async () => {
    try {
      const response = await api.get('/access-control/disciplines');
      setDisciplines(response.data);
    } catch (error) {
      console.error('Failed to fetch disciplines:', error);
    }
  };

  const fetchAccessLevel = async () => {
    try {
      const response = await api.get('/access-control/user-access-level');
      setAccessLevel(response.data);
    } catch (error) {
      console.error('Failed to fetch access level:', error);
    }
  };

  const fetchAssessments = async () => {
    try {
      const params = {};
      if (typeFilter !== 'all') params.assessment_type = typeFilter;
      if (disciplineFilter !== 'all') params.discipline = disciplineFilter;
      const response = await api.get('/assessments', { params });
      setAssessments(response.data);
    } catch (error) {
      console.error('Failed to fetch assessments:', error);
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomTemplates = async () => {
    try {
      const response = await api.get('/assessments/templates/custom');
      setCustomTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch custom templates:', error);
    }
  };

  // Check if user can edit this assessment
  const canEditAssessment = (assessment) => {
    if (isMainAdmin) return true;
    if (accessLevel?.can_edit_all_documents) return true;
    if (accessLevel?.is_lead_therapist) return true;
    // Owner can edit
    if (assessment.therapist_id === user?.id || assessment.created_by === user?.id) return true;
    return false;
  };

  // Delete assessment (Main admin only)
  const handleDeleteAssessment = async (assessment) => {
    if (!isMainAdmin) {
      toast.error('Only Md Ashraful Alam Sujan can delete assessments');
      return;
    }
    
    if (!confirm(`Are you sure you want to permanently delete the assessment for "${assessment.patient_name}"?\n\nThis action cannot be undone.`)) {
      return;
    }
    
    try {
      await api.delete(`/assessments/${assessment.id}`);
      toast.success('Assessment deleted successfully');
      fetchAssessments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete assessment');
    }
  };

  // Group assessments by discipline
  const groupedAssessments = assessments.reduce((groups, assessment) => {
    const discipline = assessment.discipline || 'Other';
    if (!groups[discipline]) groups[discipline] = [];
    groups[discipline].push(assessment);
    return groups;
  }, {});

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/assessments/templates', templateForm);
      toast.success('Assessment template created successfully');
      setTemplateDialogOpen(false);
      setTemplateForm({
        name: '',
        type: 'custom',
        description: '',
        sections: [{ title: 'Section 1', fields: [{ name: '', type: 'text', required: false }] }]
      });
      fetchCustomTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    }
  };

  const addSection = () => {
    setTemplateForm({
      ...templateForm,
      sections: [...templateForm.sections, { title: `Section ${templateForm.sections.length + 1}`, fields: [{ name: '', type: 'text', required: false }] }]
    });
  };

  const addField = (sectionIndex) => {
    const newSections = [...templateForm.sections];
    newSections[sectionIndex].fields.push({ name: '', type: 'text', required: false });
    setTemplateForm({ ...templateForm, sections: newSections });
  };

  const updateSection = (sectionIndex, field, value) => {
    const newSections = [...templateForm.sections];
    newSections[sectionIndex][field] = value;
    setTemplateForm({ ...templateForm, sections: newSections });
  };

  const updateField = (sectionIndex, fieldIndex, field, value) => {
    const newSections = [...templateForm.sections];
    newSections[sectionIndex].fields[fieldIndex][field] = value;
    setTemplateForm({ ...templateForm, sections: newSections });
  };

  const removeSection = (sectionIndex) => {
    if (templateForm.sections.length > 1) {
      const newSections = templateForm.sections.filter((_, i) => i !== sectionIndex);
      setTemplateForm({ ...templateForm, sections: newSections });
    }
  };

  const removeField = (sectionIndex, fieldIndex) => {
    const newSections = [...templateForm.sections];
    if (newSections[sectionIndex].fields.length > 1) {
      newSections[sectionIndex].fields = newSections[sectionIndex].fields.filter((_, i) => i !== fieldIndex);
      setTemplateForm({ ...templateForm, sections: newSections });
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      ot_initial: 'OT Initial',
      pt_initial: 'PT Initial',
      slt_initial: 'SLT Initial',
      sensory_profile_2: 'Sensory Profile 2',
      psychological: 'Psychological',
      hand_therapy: 'Hand Therapy'
    };
    return labels[type] || type.replace(/_/g, ' ').toUpperCase();
  };

  const handlePrint = (assessment) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Assessment - ${assessment.patient_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            .header { text-align: center; border-bottom: 2px solid #1e3a5f; padding-bottom: 20px; margin-bottom: 20px; }
            .logo { font-size: 24px; font-weight: bold; color: #1e3a5f; }
            .info { background: #f8fafc; padding: 15px; margin-bottom: 20px; }
            .section { margin-bottom: 20px; }
            .section-title { font-weight: bold; color: #1e3a5f; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">ICRCL</div>
            <p>${ICRCL_NAME}</p>
            <p>${ICRCL_ADDRESS}</p>
            <p>Tel: ${ICRCL_PHONE} | Email: ${ICRCL_EMAIL}</p>
          </div>
          <h2 style="text-align: center;">${getTypeLabel(assessment.assessment_type)} ASSESSMENT</h2>
          <div class="info">
            <p><strong>Patient:</strong> ${assessment.patient_name}</p>
            <p><strong>Date:</strong> ${new Date(assessment.date).toLocaleDateString('en-GB')}</p>
            <p><strong>Therapist:</strong> ${assessment.therapist_name}</p>
            <p><strong>Status:</strong> ${assessment.status}</p>
          </div>
          <div class="section">
            <div class="section-title">Assessment Data</div>
            <pre>${JSON.stringify(assessment.form_data, null, 2)}</pre>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownload = (assessment) => {
    toast.success('Assessment PDF download started');
  };

  const handleEmail = (assessment) => {
    toast.success(`Email dialog opened for ${assessment.patient_name}`);
  };

  const handleMessage = (assessment) => {
    toast.success(`SMS dialog opened for ${assessment.patient_name}`);
  };

  const openUploadDialog = (assessment) => {
    setSelectedAssessment(assessment);
    setUploadDialogOpen(true);
  };

  const openOutputDialog = (assessment) => {
    setSelectedAssessment(assessment);
    setOutputDialogOpen(true);
  };

  const handleAssessmentOutput = async (payload) => {
    try {
      // For now, show a success message - backend PDF generation to be implemented
      if (payload.output_type === 'download') {
        toast.success(`Downloading ${payload.output_mode === 'full' ? 'full' : 'custom'} assessment PDF...`);
        // TODO: Call backend to generate PDF with selected sections
      } else if (payload.output_type === 'print') {
        toast.success('Opening print dialog...');
        window.print();
      } else if (payload.output_type === 'email') {
        toast.success(`Assessment will be emailed to ${payload.email_to}`);
        // TODO: Call backend to send email with PDF attachment
      }
    } catch (error) {
      toast.error('Failed to process assessment output');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">Assessments</h1>
          <p className="text-slate-500 mt-1">Clinical assessment records</p>
        </div>
        <div className="flex gap-2">
          {(user?.role === 'director' || user?.role === 'clinical_lead') && (
            <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="create-template-btn">
                  <FilePlus className="w-4 h-4 mr-2" />
                  Create Assessment Form
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Assessment Form Template</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateTemplate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Template Name *</Label>
                      <Input 
                        value={templateForm.name} 
                        onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})} 
                        placeholder="e.g., Paediatric OT Assessment"
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type *</Label>
                      <Select value={templateForm.type} onValueChange={(v) => setTemplateForm({...templateForm, type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ot">Occupational Therapy</SelectItem>
                          <SelectItem value="slt">Speech & Language</SelectItem>
                          <SelectItem value="pt">Physiotherapy</SelectItem>
                          <SelectItem value="psychology">Psychology</SelectItem>
                          <SelectItem value="nutrition">Nutrition</SelectItem>
                          <SelectItem value="hand_therapy">Hand Therapy</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea 
                      value={templateForm.description} 
                      onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})} 
                      placeholder="Brief description of this assessment form..."
                      rows={2}
                    />
                  </div>

                  {/* Sections */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-base font-semibold">Form Sections</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addSection}>
                        <Plus className="w-3 h-3 mr-1" />
                        Add Section
                      </Button>
                    </div>

                    {templateForm.sections.map((section, sectionIndex) => (
                      <Card key={sectionIndex} className="p-4 bg-slate-50">
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Input 
                              value={section.title}
                              onChange={(e) => updateSection(sectionIndex, 'title', e.target.value)}
                              placeholder="Section Title"
                              className="font-medium"
                            />
                            {templateForm.sections.length > 1 && (
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeSection(sectionIndex)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                          
                          {/* Fields */}
                          <div className="space-y-2 pl-4">
                            {section.fields.map((field, fieldIndex) => (
                              <div key={fieldIndex} className="flex items-center gap-2">
                                <Input 
                                  value={field.name}
                                  onChange={(e) => updateField(sectionIndex, fieldIndex, 'name', e.target.value)}
                                  placeholder="Field name"
                                  className="flex-1"
                                />
                                <Select 
                                  value={field.type} 
                                  onValueChange={(v) => updateField(sectionIndex, fieldIndex, 'type', v)}
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="text">Text</SelectItem>
                                    <SelectItem value="textarea">Text Area</SelectItem>
                                    <SelectItem value="number">Number</SelectItem>
                                    <SelectItem value="date">Date</SelectItem>
                                    <SelectItem value="select">Dropdown</SelectItem>
                                    <SelectItem value="checkbox">Checkbox</SelectItem>
                                    <SelectItem value="radio">Radio</SelectItem>
                                  </SelectContent>
                                </Select>
                                <label className="flex items-center gap-1 text-xs">
                                  <input 
                                    type="checkbox" 
                                    checked={field.required}
                                    onChange={(e) => updateField(sectionIndex, fieldIndex, 'required', e.target.checked)}
                                  />
                                  Required
                                </label>
                                {section.fields.length > 1 && (
                                  <Button type="button" variant="ghost" size="icon" onClick={() => removeField(sectionIndex, fieldIndex)}>
                                    <Trash2 className="w-3 h-3 text-red-400" />
                                  </Button>
                                )}
                              </div>
                            ))}
                            <Button type="button" variant="ghost" size="sm" onClick={() => addField(sectionIndex)} className="text-xs">
                              <Plus className="w-3 h-3 mr-1" />
                              Add Field
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-[#1e3a5f]">Create Template</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
          <Button
            onClick={() => navigate('/assessments/new')}
            className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full"
            data-testid="new-assessment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Assessment
          </Button>
        </div>
      </div>

      {/* Custom Templates */}
      {customTemplates.length > 0 && (
        <Card className="card-icrcl">
          <CardHeader>
            <CardTitle className="text-lg text-[#1e3a5f]">Custom Assessment Forms</CardTitle>
            <CardDescription>Assessment templates created by your team</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {customTemplates.map((template) => (
                <div key={template.id} className="p-3 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 cursor-pointer" onClick={() => navigate(`/assessments/new?template=${template.id}`)}>
                  <p className="font-medium text-blue-800">{template.name}</p>
                  <p className="text-xs text-blue-600">{template.type} | {template.sections?.length || 0} sections</p>
                  <p className="text-xs text-slate-500 mt-1">Created by {template.created_by}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="card-icrcl">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <Filter className="w-5 h-5 text-slate-400" />
            <div className="flex-1 max-w-xs">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="ot_initial">OT Initial</SelectItem>
                  <SelectItem value="pt_initial">PT Initial</SelectItem>
                  <SelectItem value="slt_initial">SLT Initial</SelectItem>
                  <SelectItem value="sensory_profile_2">Sensory Profile 2</SelectItem>
                  <SelectItem value="psychological">Psychological</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 max-w-xs">
              <Select value={disciplineFilter} onValueChange={setDisciplineFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by discipline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Disciplines</SelectItem>
                  {disciplines.map((disc) => (
                    <SelectItem key={disc.id} value={disc.code}>{disc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {accessLevel && !accessLevel.can_edit_all_documents && (
              <Badge variant="outline" className="text-xs">
                {accessLevel.is_lead_therapist ? 'Lead Therapist' : 'View Only'}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Discipline Folders View */}
      {disciplineFilter === 'all' && Object.keys(groupedAssessments).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
          {Object.entries(groupedAssessments).map(([discipline, items]) => (
            <Card 
              key={discipline} 
              className="card-icrcl cursor-pointer hover:border-[#2a9d8f] transition-colors"
              onClick={() => setDisciplineFilter(discipline)}
            >
              <CardContent className="p-4 text-center">
                <FolderOpen className="w-10 h-10 mx-auto mb-2 text-[#2a9d8f]" />
                <p className="font-medium text-[#1e3a5f]">{discipline}</p>
                <p className="text-sm text-slate-500">{items.length} assessments</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Assessments Table */}
      <Card className="card-icrcl">
        <CardHeader className="pb-2">
          {disciplineFilter !== 'all' && (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setDisciplineFilter('all')}>
                ← All Disciplines
              </Button>
              <Badge className="bg-[#2a9d8f]">{disciplineFilter}</Badge>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : assessments.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No assessments found</p>
              <Button
                variant="link"
                className="text-[#2a9d8f]"
                onClick={() => navigate('/assessments/new')}
              >
                Create your first assessment
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-icrcl">
                  <TableHead>Patient</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Discipline</TableHead>
                  <TableHead>Therapist</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assessments.map((assessment) => (
                  <TableRow key={assessment.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#2a9d8f]/10 flex items-center justify-center">
                          <ClipboardList className="w-5 h-5 text-[#2a9d8f]" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{assessment.patient_name || 'Unknown'}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {getTypeLabel(assessment.assessment_type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {assessment.discipline ? (
                        <Badge variant="secondary" className="text-xs">
                          {assessment.discipline}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {assessment.therapist_name || 'Unknown'}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {new Date(assessment.date).toLocaleDateString('en-GB')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          (assessment.status === 'completed' || assessment.has_uploaded_document) ? 'badge-completed' :
                          assessment.status === 'draft' ? 'badge-pending' :
                          'badge-active'
                        }>
                          {assessment.has_uploaded_document ? 'COMPLETE' : assessment.status}
                        </Badge>
                        {assessment.has_uploaded_document && (
                          <Upload className="w-3 h-3 text-green-600" title="Has uploaded document" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/assessments/${assessment.id}`)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid="assessment-actions-menu">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openOutputDialog(assessment)}>
                              <FileOutput className="w-4 h-4 mr-2" />
                              <span className="font-medium">Print / Download / Email</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handlePrint(assessment)}>
                              <Printer className="w-4 h-4 mr-2" />
                              Quick Print (Full Form)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownload(assessment)}>
                              <Download className="w-4 h-4 mr-2" />
                              Quick Download (Full PDF)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEmail(assessment)}>
                              <Mail className="w-4 h-4 mr-2" />
                              Quick Email
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleMessage(assessment)}>
                              <MessageSquare className="w-4 h-4 mr-2" />
                              Send SMS
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => openUploadDialog(assessment)}>
                              <Upload className="w-4 h-4 mr-2" />
                              Upload Documents
                            </DropdownMenuItem>
                            {(isMainAdmin || canEditAssessment(assessment)) && (
                              <>
                                <DropdownMenuSeparator />
                                {isMainAdmin && (
                                  <DropdownMenuItem 
                                    onClick={() => handleDeleteAssessment(assessment)}
                                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                    data-testid="delete-assessment-btn"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Assessment
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            {!canEditAssessment(assessment) && (
                              <div className="px-2 py-1.5 text-xs text-slate-500 italic">
                                View only - contact Lead Therapist to edit
                              </div>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Assessment Documents Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">
              <FolderOpen className="w-5 h-5 inline mr-2" />
              Assessment Documents - {selectedAssessment?.patient_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedAssessment && (
            <div className="space-y-6">
              {/* Drag & Drop Upload Zone */}
              <DragDropUploader
                entityType="assessment"
                entityId={selectedAssessment.id}
                onUploadComplete={() => {}}
                acceptTypes="all"
                title="Upload Assessment Documents"
                description="Drag & drop existing reports, test outputs, or clinical photos"
              />

              {/* Existing Attachments */}
              <AttachmentsList
                entityType="assessment"
                entityId={selectedAssessment.id}
                title="Attached Documents"
                showUploadButton={false}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Section Selection Dialog for Print/Download/Email */}
      <SectionSelectionDialog
        open={outputDialogOpen}
        onOpenChange={setOutputDialogOpen}
        assessment={selectedAssessment}
        onOutput={handleAssessmentOutput}
        api={api}
      />
    </div>
  );
}
